import type { ZodType } from 'zod'
import { apiClient, refreshSession } from './client'

// RFC7807-подобная ошибка бэка → нормализованный ApiError.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
    message?: string,
  ) {
    super(message ?? `API ${status}`)
    this.name = 'ApiError'
  }
}

function toApiError(status: number, data: unknown): ApiError {
  const problem = (data && typeof data === 'object' ? data : {}) as {
    code?: string
    title?: string
    detail?: string
  }
  return new ApiError(status, problem.code, problem.detail ?? problem.title)
}

// 401 читается как «access протух», пока refresh не сказал обратное: одна
// попытка продлить сессию и один повтор того же вызова (openapi-fetch соберёт
// новый Request — метод, путь, query и тело сериализуются заново). Refresh
// отказал — 401 уходит наверх как есть: гостю это легальный ответ «ты не
// сотрудник», сотруднику — конец сессии, и гейт страницы уведёт на SSO.
async function withRefresh<R extends { response: Response }>(call: () => Promise<R>): Promise<R> {
  const res = await call()
  if (res.response.status !== 401) return res
  return (await refreshSession()) ? call() : res
}

// Тонкая обёртка над untyped openapi-fetch (Paths=any до типизации клиента):
// путь интерполируем сами, ответ валидируем Zod-схемой (any→unknown безопасно).
// Пути с параметрами — уже собранные строки (`/api/meetings/${id}`).
// Тело не-2xx ответа openapi-fetch кладёт в res.error (res.data пуст) —
// problem+json для ApiError берём оттуда.

export async function apiGet<T>(
  path: string,
  schema: ZodType<T>,
  query?: Record<string, string>,
): Promise<T> {
  const res = await withRefresh(() =>
    apiClient.GET(path as never, (query ? { params: { query } } : {}) as never),
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return schema.parse(res.data)
}

// Файловый ответ ручки (CSV-выгрузка): тело не JSON и схемой не проверяется —
// байты уходят прямо в скачивание.
export async function apiGetBlob(path: string, query?: Record<string, string>): Promise<Blob> {
  const res = await withRefresh(() =>
    apiClient.GET(
      path as never,
      {
        ...(query ? { params: { query } } : {}),
        parseAs: 'blob',
      } as never,
    ),
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  // Untyped клиент (Paths=any) выводит data как undefined — реальный тип задаёт
  // parseAs, поэтому приведение идёт через unknown.
  return res.data as unknown as Blob
}

export async function apiPost<T>(path: string, schema: ZodType<T>, body?: unknown): Promise<T> {
  const res = await withRefresh(() =>
    apiClient.POST(path as never, (body === undefined ? {} : { body }) as never),
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return schema.parse(res.data)
}

export async function apiPatchVoid(path: string, body?: unknown): Promise<void> {
  const res = await withRefresh(() =>
    apiClient.PATCH(path as never, (body === undefined ? {} : { body }) as never),
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
}

export async function apiPutVoid(path: string, body?: unknown): Promise<void> {
  const res = await withRefresh(() =>
    apiClient.PUT(path as never, (body === undefined ? {} : { body }) as never),
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
}

export async function apiPostVoid(path: string, body?: unknown): Promise<void> {
  const res = await withRefresh(() =>
    apiClient.POST(path as never, (body === undefined ? {} : { body }) as never),
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
}

export async function apiDelete(path: string): Promise<void> {
  const res = await withRefresh(() => apiClient.DELETE(path as never, {} as never))
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
}

// Сырой upload (octet-stream, не multipart): body — File как есть, реальный
// MIME в Content-Type, метаданные в query. Идёт через apiClient — CSRF-
// middleware и cookie-auth сохраняются.
export async function apiUpload<T>(
  path: string,
  schema: ZodType<T>,
  file: File,
  query?: Record<string, string>,
): Promise<T> {
  const res = await withRefresh(() =>
    apiClient.POST(
      path as never,
      {
        body: file,
        bodySerializer: (b: unknown) => b,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        ...(query ? { params: { query } } : {}),
      } as never,
    ),
  )
  if (!res.response.ok) throw toApiError(res.response.status, res.error)
  return schema.parse(res.data)
}
